import React, { useState } from 'react';
import {
  Typography,
  TextField,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Box,
  SelectChangeEvent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Alert from '@mui/lab/Alert';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
}));

const FormField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ExpiryFields = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

// Generate years for expiry select
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < 10; i++) {
    years.push(currentYear + i);
  }
  return years;
};

// Generate months for expiry select
const generateMonthOptions = () => {
  const months = [];
  for (let i = 1; i <= 12; i++) {
    months.push(i);
  }
  return months;
};

interface PaymentFormProps {
  onPaymentMethodSelected: (paymentMethod: any) => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ onPaymentMethodSelected }) => {
  // Form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [cvv, setCvv] = useState('');
  
  // Form validation
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<any>(null);
  
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    // Validate card number (simple length check for demo)
    if (!cardNumber || cardNumber.length < 16) {
      newErrors.cardNumber = 'Please enter a valid card number';
    }
    
    // Validate cardholder name
    if (!cardholderName) {
      newErrors.cardholderName = 'Please enter the cardholder name';
    }
    
    // Validate expiry
    if (!expiryMonth) {
      newErrors.expiryMonth = 'Required';
    }
    
    if (!expiryYear) {
      newErrors.expiryYear = 'Required';
    }
    
    // Validate CVV
    if (!cvv || cvv.length < 3) {
      newErrors.cvv = 'Please enter a valid CVV';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, this would be where you would send the card details
      // to a payment processor API like Stripe using their secure methods.
      // For this demo, we'll simulate a successful payment method creation.
      
      const mockPaymentMethod = {
        id: `pm_${Math.random().toString(36).substring(2, 15)}`,
        type: 'card',
        card: {
          brand: 'visa',
          last4: cardNumber.slice(-4),
          exp_month: Number(expiryMonth),
          exp_year: Number(expiryYear),
        },
        billing_details: {
          name: cardholderName,
        },
      };
      
      setPaymentMethod(mockPaymentMethod);
      // Pass the payment method up to the parent component
      onPaymentMethodSelected(mockPaymentMethod);
      setFormSubmitted(true);
    }
  };
  
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits
    const value = e.target.value.replace(/\D/g, '');
    setCardNumber(value);
  };
  
  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits
    const value = e.target.value.replace(/\D/g, '');
    setCvv(value);
  };
  
  // Reset form and add a new payment method
  const handleAddNewCard = () => {
    setCardNumber('');
    setCardholderName('');
    setExpiryMonth('');
    setExpiryYear('');
    setCvv('');
    setPaymentMethod(null);
    setFormSubmitted(false);
    setErrors({});
  };
  
  return (
    <Box sx={{ padding: 3 }}>
      {formSubmitted && paymentMethod ? (
        <>
          <Typography variant="h6">Payment Method</Typography>
          <StyledPaper>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  <strong>{paymentMethod.card.brand.toUpperCase()}</strong> •••• {paymentMethod.card.last4}
                </Typography>
                <Typography variant="body2">
                  {paymentMethod.billing_details.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expires {paymentMethod.card.exp_month}/{paymentMethod.card.exp_year}
                </Typography>
              </Grid>
            </Grid>
          </StyledPaper>
          
          <SubmitButton 
            variant="outlined" 
            color="primary" 
            onClick={handleAddNewCard}
          >
            Use a Different Card
          </SubmitButton>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Card Information</Typography>
            </Grid>
            
            <Grid item xs={12}>
              <FormField
                label="Card Number"
                value={cardNumber}
                onChange={handleCardNumberChange}
                fullWidth
                error={!!errors.cardNumber}
                helperText={errors.cardNumber}
                inputProps={{ maxLength: 16 }}
                placeholder="1234 5678 9012 3456"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormField
                label="Cardholder Name"
                value={cardholderName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCardholderName(e.target.value)}
                fullWidth
                error={!!errors.cardholderName}
                helperText={errors.cardholderName}
                placeholder="John Doe"
              />
            </Grid>
            
            <Grid item xs={8}>
              <ExpiryFields>
                <FormControl 
                  error={!!errors.expiryMonth} 
                  fullWidth
                >
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={expiryMonth}
                    onChange={(e: SelectChangeEvent) => setExpiryMonth(e.target.value)}
                    label="Month"
                  >
                    {generateMonthOptions().map((month) => (
                      <MenuItem key={month} value={month}>
                        {month.toString().padStart(2, '0')}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.expiryMonth && <FormHelperText>{errors.expiryMonth}</FormHelperText>}
                </FormControl>
                
                <FormControl 
                  error={!!errors.expiryYear} 
                  fullWidth
                >
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={expiryYear}
                    onChange={(e: SelectChangeEvent) => setExpiryYear(e.target.value)}
                    label="Year"
                  >
                    {generateYearOptions().map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.expiryYear && <FormHelperText>{errors.expiryYear}</FormHelperText>}
                </FormControl>
              </ExpiryFields>
            </Grid>
            
            <Grid item xs={4}>
              <FormField
                label="CVV"
                value={cvv}
                onChange={handleCvvChange}
                fullWidth
                error={!!errors.cvv}
                helperText={errors.cvv}
                inputProps={{ maxLength: 3 }}
                placeholder="123"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                This is a demo payment form. No real payments will be processed.
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
              >
                Add Payment Method
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Box>
  );
};

export default PaymentForm;
